import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { IMG } from "@/lib/site";
import { useProduct } from "@/context/ProductContext";
import { formatGBP } from "@/lib/formatGBP";
import { MotionSection } from "@/components/MotionSection";

export default function ProductSection() {
  const { product, loading, error } = useProduct();

  return (
    <MotionSection
      id="product"
      data-testid="section-product"
      className="bg-white py-20 lg:py-28"
    >
      <div className="container-pc">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <figure className="rounded-2xl bg-ice-light overflow-hidden border border-silver/40 shadow-card">
            <img
              src={IMG.lifestyle}
              alt="PeptiChill cooler on a white marble countertop in a premium home setting"
              className="block w-full max-w-full h-full object-cover"
              loading="lazy"
              width="900"
              height="700"
            />
          </figure>

          <div>
            <p className="eyebrow">Now available — pre-order</p>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[44px] font-bold text-navy tracking-tighter leading-tight">
              {product?.name || "PeptiChill Peptide Cooler Fridge"}
            </h2>

            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-navy/80 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-cyan inline-block" aria-hidden="true" />
                Pre-order price
                <span className="w-1 h-1 rounded-full bg-cyan inline-block" aria-hidden="true" />
              </div>
              {loading && (
                <span
                  className="mt-2 inline-block w-40 h-10 lg:h-12 rounded-xl bg-navy/10 animate-pulse"
                  data-testid="product-price-skeleton"
                  aria-hidden="true"
                />
              )}
              {!loading && product && (
                <p
                  className="mt-2 text-3xl lg:text-4xl font-semibold text-navy tabular-nums"
                  data-testid="product-price"
                >
                  {formatGBP(product.price_pennies)}
                </p>
              )}
              {!loading && !product && error && (
                <p className="mt-2 text-sm text-muted italic" data-testid="product-price-unavailable">
                  Price unavailable — please refresh
                </p>
              )}
              <p className="mt-2 text-xs text-muted">Pre-order price · Ships July 2026</p>
            </div>

            <p className="mt-5 text-base text-muted leading-relaxed max-w-lg">
              {product?.short_description || (
                <span className="inline-block w-full max-w-lg h-20 rounded bg-navy/5 animate-pulse" aria-hidden="true" />
              )}
            </p>

            <ul className="mt-7 space-y-3 max-w-lg" data-testid="product-bullets">
              {(product?.bullet_points || []).map((b) => (
                <li key={b} className="flex gap-3 text-[15px] text-ink">
                  <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-navy text-cyan shrink-0">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
              {loading && [0, 1, 2, 3].map((i) => (
                <li key={i} className="h-4 w-3/4 rounded bg-navy/5 animate-pulse" aria-hidden="true" />
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/product"
                data-testid="product-cta-add-cart"
                className="inline-flex items-center justify-center rounded-full bg-navy text-white px-7 py-3 text-sm font-semibold tracking-wide hover:brightness-110 transition shadow-card"
              >
                Add to Cart
              </Link>
              <Link
                to="/product"
                data-testid="product-cta-buy-now"
                className="inline-flex items-center justify-center rounded-full border border-navy text-navy px-7 py-3 text-sm font-semibold tracking-wide hover:bg-ice transition"
              >
                Pre-order Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MotionSection>
  );
}
